import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import { FontAwesome6 } from '@expo/vector-icons';
import { colors, typography } from '../../styles/theme';

interface HowToPlayProps {
  visible: boolean;
  onClose: () => void;
  onBack?: () => void;
}

export default function HowToPlayModal({ visible, onClose, onBack }: HowToPlayProps) {
  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      header={onBack ? (
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={onBack} style={styles.backButton}>
            <FontAwesome6 name="arrow-left" size={20} color={colors.text} solid />
          </TouchableOpacity>
          <Text style={[modalStyles.headerText, { flex: 1, marginBottom: 0 }]}>How to Play & FAQ</Text>
          <View style={{ width: 44 }} />
        </View>
      ) : (
        <Text style={modalStyles.headerText}>How to Play & FAQ</Text>
      )}
      footer={(
        <ModalStickyFooter>
          <HoverButton
            style={modalStyles.primaryButton}
            hoverStyle={modalStyles.primaryButtonHover}
            textStyle={modalStyles.primaryButtonText}
            onPress={onClose}
          >
            Got It!
          </HoverButton>
        </ModalStickyFooter>
      )}
    >
      <View style={styles.container}>

        {/* --- HOW TO PLAY SECTION --- */}
        <Text style={styles.sectionTitle}>How to Play</Text>

        <Text style={styles.itemTitle}>1. Select a Color</Text>
        <Text style={styles.itemBody}>Tap on a color swatch at the bottom to select which layer you want to paint.</Text>

        <Text style={styles.itemTitle}>2. Find the Color</Text>
        <Text style={styles.itemBody}>Look through your camera and find objects in your environment that match the selected color.</Text>

        <Text style={styles.itemTitle}>3. Capture</Text>
        <Text style={styles.itemBody}>When you've found a good match, tap the camera button to capture that section of your image.</Text>

        <Text style={styles.itemTitle}>4. Complete All Layers</Text>
        <Text style={styles.itemBody}>Continue selecting colors and capturing images until all layers are filled. Watch your artwork come to life!</Text>

        <View style={styles.divider} />

        {/* --- FAQ SECTION --- */}
        <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>

        <Text style={styles.itemTitle}>Are you saving my photos or recording my camera?</Text>
        <Text style={styles.itemBody}>No. The camera feed is strictly processed locally on your device. When you take a picture, the color-matching math happens entirely within your browser. I do not save, upload, or have access to your camera roll or live feed.</Text>

        <Text style={styles.itemTitle}>Why is the color matching not working?</Text>
        <Text style={styles.itemBody}>Cameras see light, not just pigment. A red apple in a dark room might look black to your phone. If you're stuck, try turning on a lamp or moving closer to a window. If a puzzle is too busy/textured, try toggling "Easy Mode" in the main menu.</Text>

        <Text style={styles.itemTitle}>How do custom puzzles work?</Text>
        <Text style={styles.itemBody}>When you upload an image, the servers generate a playable SVG puzzle and securely store it. You can have up to 5 custom puzzles active at a time. Creating a 6th will automatically overwrite your oldest one. Anyone with your custom link can play it.</Text>

        <Text style={styles.itemTitle}>Will I lose my progress if I close the tab?</Text>
        <Text style={styles.itemBody}>Your daily streak, puzzle progress, and custom packs are saved securely to your browser. If you switch devices or accidentally clear your browser data, you can instantly restore your premium packs using the unlock link sent to your email.</Text>

      </View>
    </BaseModal>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    width: '100%',
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    marginBottom: 20
  },
  sectionTitle: {
    ...typography.header,
    fontSize: 22,
    color: colors.primary,
    marginTop: 8,
    marginBottom: 16
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
    width: '100%',
    marginVertical: 24,
  },
  itemTitle: {
    ...typography.bodyBold,
    fontSize: 18,
    color: colors.text,
    marginBottom: 6
  },
  itemBody: {
    ...typography.body,
    color: colors.textMuted,
    marginBottom: 20,
    lineHeight: 22
  },
});